import { useState, useRef, useMemo } from "react";
import { useQuery, useMutation } from "react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { 
  CheckCircle, 
  Download, 
  Users, 
  TrendingUp,
  AlertCircle,
  Printer,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Trash2
} from "lucide-react";
import DataTable from "@/components/datatable/Datatable";
import { ColumnDef } from "@tanstack/react-table";
import DataTableColumnHeader from "@/components/datatable/DataTableColumnHeader";
import useConfirmModal from "@/hooks/useConfirmModal";
import ConfirmModal from "@/components/modal/ConfirmModal";
import { useReactToPrint } from "react-to-print";
import { Navigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MonthlySavingsPayroll from "./MonthlySavingsPayroll";
import LoanRepaymentsPayroll from "@/components/loans/LoanRepaymentsPayroll";

interface PayrollTeacher {
  teacherId: number;
  teacherCode: string;
  teacherName: string;
  declaredShares: number;
  shareValue: number;
  contributionAmount: number;
  lastDeclarationDate: string | null;
  hasDelcared: boolean;
  totalSavings?: number;
  groupRole?: string;
  autoDeclared?: boolean;
  autoDeclaredSource?: "lastMonth" | "minimum" | null;
  autoDeclaredNote?: string | null;
  declarationStatus?: string;
  hasDirectContribution?: boolean;
  previousShareCount?: number | null;
  previousDeclarationMonth?: number | null;
  previousDeclarationYear?: number | null;
}

interface PayrollReport {
  group: {
    id: number;
    name: string;
    shareValue: number;
    salaryPaymentDay: number;
  };
  payroll: PayrollTeacher[];
  summary: {
    totalTeachers: number;
    teachersWithDeclarations: number;
    totalContributions: number;
  };
  reportDate: string;
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

// Helper function to clean group name (remove "Kigali Primary School")
const cleanGroupName = (groupName: string): string => {
  if (!groupName) return "";
  return groupName
    .replace(/Kigali Primary School/gi, "")
    .replace(/\s+/g, " ")
    .trim();
};

export default function HRPayroll() {
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  
  // Check if user is a group leader (BEFORE any hooks)
  const isGroupLeader = currentUser.isGroupLeader || currentUser.leadershipRole;
  
  // Redirect if not a group leader (BEFORE any hooks)
  if (!isGroupLeader) {
    return <Navigate to="/dashboard" replace />;
  }

  const [groupId] = useState(1); // Default group
  const [approver] = useState(
    currentUser.fullName || 
    `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() || 
    currentUser.email || 
    'Admin'
  );
  const clearModal = useConfirmModal();
  const printRef = useRef<HTMLDivElement>(null);
  
  // Month/Year selection (defaults to current month/year)
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  
  // Generate years array (current year and past 5 years)
  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - i);
  
  // Table state
  const [columnFilters, setColumnFilters] = useState<any[]>([]);
  const [sorting, setSorting] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({ pageIndex: 0, pageSize: 10 }); // 10 members per page
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"contributions" | "loans" | "monthly-savings" | "loan-repayments">("monthly-savings");
  
  const leadershipRole = currentUser.leadershipRole || "Group Leader";

  // Fetch payroll report
  const { data: payrollData, isLoading, refetch } = useQuery<{ data: PayrollReport }>(
    ["payroll", groupId, selectedMonth, selectedYear],
    async () => {
      const { data } = await api.get(
        `/shares/payroll?groupId=${groupId}&month=${selectedMonth}&year=${selectedYear}`
      );
      return data;
    },
    {
      enabled: !!groupId,
    }
  );

  // Fallback members list: used to always show members even when payroll report is empty/unavailable
  const { data: membersData, isLoading: isLoadingMembers } = useQuery<any[]>(
    ["payroll-members-fallback", groupId],
    async () => {
      const { data } = await api.get("/members", {
        params: { page: 1, limit: 500 },
      });
      const members = data?.results || data?.data || [];
      return Array.isArray(members) ? members : [];
    },
    {
      enabled: !!groupId,
    }
  );

  // Fetch all loan payment declarations for HR Payroll (both approved and pending)

  // Clear payroll data mutation
  const clearPayrollMutation = useMutation(
    async () => {
      const { data } = await api.post("/shares/payroll/clear", {
        groupId,
        month: selectedMonth,
        year: selectedYear,
      });
      return data;
    },
    {
      onSuccess: async (data) => {
        toast.success(data.message || "Payroll data cleared successfully!");
        // Refetch to get updated data
        await refetch();
        clearModal.close();
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || "Failed to clear payroll data";
        toast.error(errorMessage);
        clearModal.close();
      },
    }
  );

  // Fetch all loan payment declarations for HR Payroll (both approved and pending)
  const { data: loanPaymentDeclarationsData, refetch: refetchLoanPayments } = useQuery(
    ["loan-payment-declarations-payroll", groupId, selectedMonth, selectedYear],
    async () => {
      const { data } = await api.get(
        `/loan-payment-declarations/payroll?groupId=${groupId}&month=${selectedMonth}&year=${selectedYear}`
      );
      return data;
    },
    {
      enabled: !!groupId,
    }
  );

  const loanPaymentModal = useConfirmModal();

  // Approve loan payment declarations mutation
  const approveLoanPaymentsMutation = useMutation(
    async () => {
      const { data } = await api.post("/loan-payment-declarations/approve", {
        groupId,
        month: selectedMonth,
        year: selectedYear,
        approvedBy: approver,
      });
      return data;
    },
    {
      onSuccess: async (data) => {
        toast.success(data.message || "Loan payment declarations approved successfully!");
        await refetchLoanPayments();
        loanPaymentModal.close();
      },
      onError: (error: any) => {
        const errorMessage = error.response?.data?.message || "Failed to approve loan payment declarations";
        toast.error(errorMessage);
        loanPaymentModal.close();
      },
    }
  );

  // Initialize print handler (MUST be before any early returns)
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
      @page {
        margin: 1cm;
      }
      @media print {
        body { -webkit-print-color-adjust: exact; }
        a[href]:after { content: none !important; }
      }
    `,
  });


  // Filter payroll data based on search query (needs to run every render for hook order)
  const fallbackReport: PayrollReport | null = useMemo(() => {
    if (!membersData || membersData.length === 0) return null;

    const payrollRows: PayrollTeacher[] = membersData.map((member: any) => ({
      teacherId: Number(member.id),
      teacherCode: member.memberCode || member.teacherCode || `M-${member.id}`,
      teacherName:
        member.fullNames ||
        member.teacherName ||
        [member.firstName, member.lastName].filter(Boolean).join(" ").trim() ||
        "Member",
      declaredShares: 0,
      shareValue: Number(member.shareValue || 1000),
      contributionAmount: 0,
      lastDeclarationDate: null,
      hasDelcared: false,
      totalSavings: Number(member.totalSavings || member.currentSavings || 0),
      groupRole:
        member.leadershipRole ||
        (typeof member.role === "string" ? member.role : member.role?.name) ||
        "Member",
      declarationStatus: "Pending",
      autoDeclared: false,
      autoDeclaredSource: null,
      autoDeclaredNote: null,
      previousShareCount: null,
      previousDeclarationMonth: null,
      previousDeclarationYear: null,
    }));

    return {
      group: {
        id: Number(groupId),
        name: currentUser?.group?.name || "Group",
        shareValue: 1000,
        salaryPaymentDay: 28,
      },
      payroll: payrollRows,
      summary: {
        totalTeachers: payrollRows.length,
        teachersWithDeclarations: 0,
        totalContributions: 0,
      },
      reportDate: new Date().toISOString(),
    };
  }, [membersData, groupId, currentUser?.group?.name]);

  const report = payrollData?.data || fallbackReport;

  const filteredPayroll = useMemo(() => {
    const currentReport = report;
    if (!currentReport || !currentReport.payroll || !Array.isArray(currentReport.payroll)) return [];
    return currentReport.payroll.filter((teacher) => {
      if (!searchQuery.trim()) return true;
      
      const query = searchQuery.toLowerCase();
      const statusText =
        teacher.declarationStatus?.toLowerCase() ||
        (teacher.hasDelcared
          ? "declared"
          : teacher.autoDeclared
          ? "auto"
          : "pending");
      return (
        teacher.teacherName.toLowerCase().includes(query) ||
        teacher.declaredShares.toString().includes(query) ||
        (teacher.totalSavings || 0).toString().includes(query) ||
        (teacher.groupRole || "member").toLowerCase().includes(query) ||
        statusText.includes(query)
      );
    });
  }, [report, searchQuery]);

  // Paginate the filtered data
  const startIndex = pagination.pageIndex * pagination.pageSize;
  const endIndex = startIndex + pagination.pageSize;
  const paginatedPayroll = filteredPayroll.slice(startIndex, endIndex);

  // Column definitions for loan payment declarations table
  const loanPaymentDeclarationColumns: ColumnDef<any>[] = [
    {
      accessorKey: "teacherName",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Member Name" />
      ),
      cell: ({ row }) => (
        <div className="text-sm font-medium">{row.getValue("teacherName")}</div>
      ),
    },
    {
      accessorKey: "teacherCode",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Member Code" />
      ),
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground">{row.getValue("teacherCode") || "N/A"}</div>
      ),
    },
    {
      accessorKey: "loanNumber",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Loan Being Paid" />
      ),
      cell: ({ row }) => {
        const loanNumber = row.getValue("loanNumber") as string;
        const loanPurpose = row.original.loanPurpose;
        const loanId = row.original.loanId;
        const loanType = row.original.loanType;
        const isGeneralPayment = loanNumber === "General Payment" || !loanId;
        
        return (
          <div className="text-sm">
            {isGeneralPayment ? (
              <div className="font-medium text-muted-foreground italic">{loanNumber}</div>
            ) : (
              <>
                <div className="font-semibold text-blue-600">{loanNumber}</div>
                {loanType ? (
                  <div className="text-xs font-medium text-purple-600 mt-0.5">{loanType}</div>
                ) : (
                  <div className="text-xs text-gray-400 mt-0.5 italic">No loan type</div>
                )}
                {loanPurpose && (
                  <div className="text-xs text-muted-foreground mt-0.5">{loanPurpose}</div>
                )}
              </>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "paymentAmount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Payment Amount" />
      ),
      cell: ({ row }) => {
        const amount = row.getValue("paymentAmount") as number;
        return (
          <div className="font-bold text-white bg-[#4dbd07] px-3 py-1.5 rounded inline-block">
            {amount?.toLocaleString() || 0} FRW
          </div>
        );
      },
    },
    {
      accessorKey: "groupLeaderApproved",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const isApproved = row.getValue("groupLeaderApproved") as boolean;
        return (
          <Badge
            variant={isApproved ? "default" : "secondary"}
            className={
              isApproved
                ? "bg-green-500 text-white"
                : "bg-yellow-500 text-white"
            }
          >
            {isApproved ? (
              <>
                <CheckCircle className="w-3 h-3 mr-1" />
                Approved
              </>
            ) : (
              <>
                <AlertCircle className="w-3 h-3 mr-1" />
                Pending
              </>
            )}
          </Badge>
        );
      },
    },
    {
      accessorKey: "groupLeaderApprovedBy",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Approved By" />
      ),
      cell: ({ row }) => {
        const approvedBy = row.getValue("groupLeaderApprovedBy") as string;
        const approvedAt = row.original.groupLeaderApprovedAt;
        if (!approvedBy) {
          return <div className="text-sm text-muted-foreground">-</div>;
        }
        return (
          <div className="text-sm">
            <div className="font-medium">{approvedBy}</div>
            {approvedAt && (
              <div className="text-xs text-muted-foreground">
                {format(new Date(approvedAt), "MMM dd, yyyy")}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Declared Date" />
      ),
      cell: ({ row }) => {
        const date = row.getValue("createdAt") as string;
        return (
          <div className="text-sm">
            {date ? format(new Date(date), "MMM dd, yyyy") : "N/A"}
          </div>
        );
      },
    },
  ];

  const columns: ColumnDef<PayrollTeacher>[] = [
    {
      accessorKey: "teacherName",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Member Name" />
      ),
      cell: ({ row }) => (
        <div className="text-sm">{row.getValue("teacherName")}</div>
      ),
    },
    {
      id: "declarationStatus",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const teacher = row.original;
        let label = teacher.declarationStatus || "Pending";
        let badgeClass =
          "text-xs px-2 py-0.5 border rounded-full font-semibold inline-flex items-center gap-1";

        if (teacher.hasDirectContribution && !teacher.autoDeclared) {
          label = "Direct Contribution";
          badgeClass += " border-blue-300 text-blue-800 bg-blue-50";
        } else if (teacher.autoDeclared) {
          label =
            teacher.autoDeclaredSource === "lastMonth"
              ? "Auto · Last month"
              : "Auto · Minimum";
          badgeClass += " border-amber-300 text-amber-800 bg-amber-50";
        } else if (teacher.hasDelcared) {
          badgeClass += " border-green-300 text-green-800 bg-green-50";
        } else {
          badgeClass += " border-gray-300 text-gray-600 bg-gray-50";
        }

        return <span className={badgeClass}>{label}</span>;
      },
    },
    {
      accessorKey: "declaredShares",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Shares" />
      ),
      cell: ({ row }) => {
        const shares = row.getValue("declaredShares") as number;
        return (
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="text-base bg-theme-primary/10 border-theme-primary text-theme-text"
            >
              {shares || 0} shares
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "totalSavings",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Total Savings" />
      ),
      cell: ({ row }) => {
        const totalSavings = row.getValue("totalSavings") as number;
        return (
          <div className="text-sm font-semibold text-theme-accent">
            {totalSavings?.toLocaleString() || "0"} FRW
          </div>
        );
      },
    },
    {
      accessorKey: "groupRole",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Group Role" />
      ),
      cell: () => (
          <span className="text-sm text-muted-foreground">Member</span>
      ),
    },
    {
      accessorKey: "contributionAmount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Contribution" />
      ),
      cell: ({ row }) => {
        const amount = row.getValue("contributionAmount") as number;
        return (
          <div className="font-bold text-theme-accent text-sm">
            {amount?.toLocaleString()} FRW
          </div>
        );
      },
    },
    {
      accessorKey: "lastDeclarationDate",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Last Declaration" />
      ),
      cell: ({ row }) => {
        const {
          lastDeclarationDate,
          autoDeclared,
          autoDeclaredNote,
          autoDeclaredSource,
        } = row.original;

        if (lastDeclarationDate) {
          return (
            <div className="text-sm flex flex-col">
              <span>{new Date(lastDeclarationDate).toLocaleDateString()}</span>
              {autoDeclared && autoDeclaredNote && (
                <span className="text-xs text-muted-foreground">{autoDeclaredNote}</span>
              )}
            </div>
          );
        }

        if (autoDeclared) {
          return (
            <div className="text-sm flex flex-col">
              <span>
                {autoDeclaredSource === "lastMonth"
                  ? "Auto (last month)"
                  : "Auto (minimum)"}
              </span>
              {autoDeclaredNote && (
                <span className="text-xs text-muted-foreground">{autoDeclaredNote}</span>
              )}
            </div>
          );
        }

        return <div className="text-sm text-muted-foreground">Pending</div>;
      },
    },
  ];

  if (isLoading && !report) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading payroll report...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-lg font-semibold">No payroll data available</p>
          <p className="text-muted-foreground mt-2">
            {isLoadingMembers
              ? "Loading members..."
              : "Unable to load payroll report and members. Please try again."}
          </p>
        </div>
      </div>
    );
  }



  const handleClearPayroll = () => {
    if (!report) return;
    
    const monthName = MONTHS[selectedMonth];
    clearModal.open({
      id: groupId,
      name: `${monthName} ${selectedYear}`,
    });
  };

  const handleExportCSV = () => {
    if (!report) return;

    const now = new Date();
    const monthYear = `${MONTHS[selectedMonth]} ${selectedYear}`;
    
    const csvData = [
      ["CAISSE D'ENTRAIDE IPRC KARONGI - CONTRIBUTION REPORT"],
      [`Month: ${monthYear}`],
      [`Group: ${cleanGroupName(report.group?.name || "")}`],
      [`Address: Karongi, Rwanda`],
      [`Phone: +250 783 610 386`],
      [`Email: ikiminakarongicollege@gmail.com`],
      [`Generated: ${format(now, 'MMMM d, yyyy')} at ${format(now, 'h:mm a')}`],
      [`Generated By: ${leadershipRole} - ${currentUser.fullName || currentUser.firstName}`],
      [],
      ["Member Name", "Status", "Shares Declared", "Total Savings (FRW)", "Group Role", "Contribution Amount (FRW)", "Last Declaration"],
      ...(report.payroll || []).map(t => [
        t.teacherName,
        t.hasDelcared
          ? "Declared"
          : t.autoDeclared
          ? t.autoDeclaredSource === "lastMonth"
            ? "Auto (last month)"
            : "Auto (minimum)"
          : "Pending",
        t.declaredShares,
        t.totalSavings || 0,
        t.groupRole || "Member",
        t.contributionAmount,
        t.lastDeclarationDate ? format(new Date(t.lastDeclarationDate), 'MM/d/yyyy') : 'Pending'
      ]),
      [],
      ["SUMMARY"],
      ["Total Members", report.summary?.totalTeachers || 0],
      ["Members with Declarations", report.summary?.teachersWithDeclarations || 0],
      ["Total Contributions (FRW)", report.summary?.totalContributions || 0],
      [],
    ];

    const csv = csvData.map(row => Array.isArray(row) ? row.map(cell => `"${cell}"`).join(",") : [row]).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `contribution_report_${monthYear.replace(/\s+/g, '_')}_${format(now, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Contribution report exported successfully!");
  };

  const handleDownloadPDF = async () => {
    if (!report) return;

    try {
      const monthYear = `${MONTHS[selectedMonth]} ${selectedYear}`;
      const startDate = format(new Date(selectedYear, selectedMonth, 1), 'MMMM d, yyyy');
      const endDate = format(new Date(selectedYear, selectedMonth + 1, 0), 'MMMM d, yyyy');
      
      // Dynamically import jsPDF and html2canvas
      const jsPDF = (await import('jspdf')).jsPDF;
      const html2canvas = (await import('html2canvas')).default;

      toast.loading("Generating PDF...", { id: "pdf-generation" });

      // Build table rows
      const tableRows = (report.payroll || []).map((teacher: PayrollTeacher, index: number) => {
        const status = teacher.hasDelcared
          ? "Declared"
          : teacher.autoDeclared
          ? teacher.autoDeclaredSource === "lastMonth"
            ? "Auto (last month)"
            : "Auto (minimum)"
          : "Pending";
        
        const rowBg = index % 2 === 0 ? '#f9f9f9' : '#ffffff';
        return `<tr style="background-color: ${rowBg};">
          <td style="border: 1px solid #ddd; padding: 8px 6px;">${teacher.teacherName || 'N/A'}</td>
          <td style="border: 1px solid #ddd; padding: 8px 6px;">${status}</td>
          <td style="border: 1px solid #ddd; padding: 8px 6px;">${teacher.declaredShares || 0} Shares</td>
          <td style="border: 1px solid #ddd; padding: 8px 6px;">${(teacher.totalSavings || 0).toLocaleString()} FRW</td>
          <td style="border: 1px solid #ddd; padding: 8px 6px;">${teacher.groupRole || 'Member'}</td>
          <td style="border: 1px solid #ddd; padding: 8px 6px;">${(teacher.contributionAmount || 0).toLocaleString()} FRW</td>
          <td style="border: 1px solid #ddd; padding: 8px 6px;">${teacher.lastDeclarationDate ? format(new Date(teacher.lastDeclarationDate), 'MM/d/yyyy') : 'Pending'}</td>
        </tr>`;
      }).join('');
      
      // Create a hidden div for PDF content
      const pdfDiv = document.createElement('div');
      pdfDiv.style.position = 'fixed';
      pdfDiv.style.left = '-9999px';
      pdfDiv.style.top = '0';
      pdfDiv.style.width = '1200px';
      pdfDiv.style.background = '#fff';
      pdfDiv.style.fontFamily = 'Arial, sans-serif';
      pdfDiv.style.padding = '40px';
      pdfDiv.innerHTML = `
        <div style="margin-bottom: 30px;">
          <div style="display: flex; align-items: center; margin-bottom: 15px;">
            <div style="width: 120px; margin-right: 20px; display: flex; flex-direction: column; align-items: center;">
              <img src={getCooperativeLogoSrc()} alt="IKIMINA Logo" style="max-width: 120px; height: auto; object-fit: contain;" />
            </div>
            <div style="flex: 1;">
              <div style="font-size: 24px; font-weight: bold; color: #000; margin-bottom: 5px;">CAISSE D'ENTRAIDE IPRC KARONGI</div>
              <div style="font-size: 18px; font-weight: bold; color: #000; margin-bottom: 5px;">Contribution Report</div>
              <div style="font-size: 14px; color: #000; margin-bottom: 0;">${startDate} - ${endDate}</div>
            </div>
          </div>
          <hr style="border: none; border-top: 3px solid #000; margin: 15px 0; width: 100%;">
          <div style="display: block; font-size: 12px; color: #000; margin-bottom: 15px;">
            <div style="display: block; margin-bottom: 5px;"><strong>Address:</strong> Karongi, Rwanda</div>
            <div style="display: block; margin-bottom: 5px;"><strong>Phone:</strong> +250 783 610 386</div>
            <div style="display: block; margin-bottom: 5px;"><strong>Email:</strong> ikiminakarongicollege@gmail.com</div>
          </div>
          <hr style="border: none; border-top: 1px solid #ccc; margin: 15px 0 20px 0; width: 100%;">
        </div>
        
        <div style="text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; color: #050500;">
          CONTRIBUTION REPORT
        </div>
        
        <div style="margin-top: 20px; padding: 15px; background-color: #f5f5f5; border-radius: 5px; font-size: 14px;">
          <h3 style="margin-bottom: 10px;">Summary</h3>
          <p><strong>Group:</strong> ${cleanGroupName(report.group?.name || "")}</p>
          <p><strong>Month:</strong> ${monthYear}</p>
          <p><strong>Total Members:</strong> ${report.summary?.totalTeachers || 0}</p>
          <p><strong>Members with Declarations:</strong> ${report.summary?.teachersWithDeclarations || 0}</p>
          <p><strong>Total Contributions:</strong> ${(report.summary?.totalContributions || 0).toLocaleString()} FRW</p>
        </div>
        
        <table style="width: 100%; border-collapse: collapse; font-size: 11px; margin-top: 20px;">
          <thead>
            <tr>
              <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Member Name</th>
              <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Status</th>
              <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Shares</th>
              <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Total Savings</th>
              <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Group Role</th>
              <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Contribution</th>
              <th style="background-color: #4dbd07; color: white; padding: 10px 6px; text-align: left; font-weight: 600; border: 1px solid #3d9a06;">Last Declaration</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
        </table>
        
        <div style="margin-top: 24px; padding: 15px; background-color: #f5f5f5; border-radius: 5px; font-size: 14px;">
          <strong>Total Members:</strong> ${report.summary?.totalTeachers || 0} | 
          <strong>Total Contributions:</strong> ${(report.summary?.totalContributions || 0).toLocaleString()} FRW | 
          <strong>Generated on:</strong> ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
        </div>
      `;
      
      document.body.appendChild(pdfDiv);

      // Use html2canvas to render the div
      const canvas = await html2canvas(pdfDiv, { scale: 2 });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'px', format: [canvas.width, canvas.height] });
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      pdf.save(`contribution_report_${monthYear.replace(/\s+/g, '_')}.pdf`);
      document.body.removeChild(pdfDiv);

      toast.dismiss("pdf-generation");
      toast.success('PDF report downloaded successfully');
    } catch (error) {
      toast.dismiss("pdf-generation");
      console.error('Error generating PDF:', error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <>
      <ConfirmModal
        title="Approve Loan Payment Declarations?"
        description={`This will approve ${loanPaymentDeclarationsData?.summary?.pending || 0} pending loan payment declaration(s) totaling ${(loanPaymentDeclarationsData?.summary?.pendingAmount || 0).toLocaleString()} FRW for ${loanPaymentDeclarationsData?.summary?.monthName || ''} ${loanPaymentDeclarationsData?.summary?.year || ''}. This action cannot be undone.`}
        meta={loanPaymentModal.meta}
        onConfirm={() => approveLoanPaymentsMutation.mutate()}
        isLoading={approveLoanPaymentsMutation.isLoading}
        open={loanPaymentModal.isOpen}
        onClose={() => loanPaymentModal.close()}
      />

      <ConfirmModal
        title="⚠️ Clear Payroll Data?"
        description={`WARNING: This will permanently delete ALL payroll data for ${clearModal.meta?.name || 'this period'}, including:
        
• All share declarations
• All contributions
• Approval status records
• Group member share counts will be updated

This action CANNOT be undone. Are you absolutely sure you want to proceed?`}
        meta={clearModal.meta}
        onConfirm={() => clearPayrollMutation.mutate()}
        isLoading={clearPayrollMutation.isLoading}
        open={clearModal.isOpen}
        onClose={() => clearModal.close()}
      />

      <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-theme-background sm:px-2 px-2">
        <div className="max-w-7xl mx-auto space-y-6 min-w-0" id="print-container" ref={printRef}>
          {/* Header */}
          <motion.div 
            className="flex items-center justify-between gap-1.5 sm:gap-4 overflow-hidden mb-4 no-print"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <h1
              className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold tracking-tight flex-shrink-0 whitespace-nowrap"
              style={{ color: "#050500" }}
            >
              HR Payroll
            </h1>
            <div className="flex items-center gap-1 sm:gap-1.5 md:gap-2 flex-shrink-0">
          {/* Month/Year Selector */}
              <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2">
                <motion.button
                  onClick={() => {
                    if (selectedMonth === 0) {
                      setSelectedMonth(11);
                      setSelectedYear(selectedYear - 1);
                    } else {
                      setSelectedMonth(selectedMonth - 1);
                    }
                  }}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <ChevronLeft className="w-4 h-4" />
                </motion.button>
                
                <div className="flex items-center gap-2">
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#4dbd07] focus:border-transparent bg-white"
                  >
                    {MONTHS.map((month, index) => (
                      <option key={index} value={index}>
                        {month}
                      </option>
                    ))}
                  </select>
                  
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#4dbd07] focus:border-transparent bg-white"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>
                
                <motion.button
                  onClick={() => {
                    const nextMonth = selectedMonth + 1;
                    if (nextMonth > 11) {
                      setSelectedMonth(0);
                      setSelectedYear(selectedYear + 1);
                    } else {
                      setSelectedMonth(nextMonth);
                    }
                  }}
                  disabled={selectedMonth === now.getMonth() && selectedYear === now.getFullYear()}
                  className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: selectedMonth === now.getMonth() && selectedYear === now.getFullYear() ? 1 : 1.1 }}
                  whileTap={{ scale: selectedMonth === now.getMonth() && selectedYear === now.getFullYear() ? 1 : 0.9 }}
                >
                  <ChevronRight className="w-4 h-4" />
                </motion.button>
              </div>
              
              <motion.button
                className="h-8 sm:h-9 rounded-md px-1.5 sm:px-2 md:px-3 text-[10px] sm:text-xs md:text-[13px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 border border-slate-300 min-w-[60px] sm:min-w-[80px] md:min-w-[100px] flex-shrink-0 bg-slate-100 hover:bg-slate-200"
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                onClick={async () => {
                  if (activeTab === "loans") {
                    // Handle loan payment declarations print/export
                    return;
                  }
                  if (!report) return;
                  
                  try {
                    const monthYear = `${MONTHS[selectedMonth]} ${selectedYear}`;
                    const startDate = format(new Date(selectedYear, selectedMonth, 1), 'MMMM d, yyyy');
                    const endDate = format(new Date(selectedYear, selectedMonth + 1, 0), 'MMMM d, yyyy');
                    
                    // Build table rows
                    const tableRows = (report.payroll || []).map((teacher: PayrollTeacher, index: number) => {
                      const status = teacher.hasDelcared
                        ? "Declared"
                        : teacher.autoDeclared
                        ? teacher.autoDeclaredSource === "lastMonth"
                          ? "Auto (last month)"
                          : "Auto (minimum)"
                        : "Pending";
                      
                      const rowBg = index % 2 === 0 ? '#f9f9f9' : '#ffffff';
                      return `<tr style="background-color: ${rowBg};">
                        <td style="border: 1px solid #ddd; padding: 8px 6px;">${teacher.teacherName || 'N/A'}</td>
                        <td style="border: 1px solid #ddd; padding: 8px 6px;">${status}</td>
                        <td style="border: 1px solid #ddd; padding: 8px 6px;">${teacher.declaredShares || 0} Shares</td>
                        <td style="border: 1px solid #ddd; padding: 8px 6px;">${(teacher.totalSavings || 0).toLocaleString()} FRW</td>
                        <td style="border: 1px solid #ddd; padding: 8px 6px;">${teacher.groupRole || 'Member'}</td>
                        <td style="border: 1px solid #ddd; padding: 8px 6px;">${(teacher.contributionAmount || 0).toLocaleString()} FRW</td>
                        <td style="border: 1px solid #ddd; padding: 8px 6px;">${teacher.lastDeclarationDate ? format(new Date(teacher.lastDeclarationDate), 'MM/d/yyyy') : 'Pending'}</td>
                      </tr>`;
                    }).join('');
                    
                    // Create print content
                    const printContent = `
                      <div class="print-header">
                        <div class="logo-container">
                          <div class="logo-box">
                            <img src={getCooperativeLogoSrc()} alt="IKIMINA Logo" />
                          </div>
                        </div>
                        <div class="company-info">
                          <div class="company-name">CAISSE D'ENTRAIDE IPRC KARONGI</div>
                          <div class="report-title">Contribution Report</div>
                          <div class="date-range">${startDate} - ${endDate}</div>
                        </div>
                      </div>
                      <hr class="separator-thick">
                      <div class="contact-info">
                        <div><strong>Address:</strong> Karongi, Rwanda</div>
                        <div><strong>Phone:</strong> 0793502827</div>
                        <div><strong>Email:</strong> caisse.iprcwest@gmail.com</div>
                      </div>
                      <hr class="separator-thin">
                      
                      <div style="text-align: center; font-size: 20px; font-weight: bold; margin: 20px 0; color: #050500;">
                        CONTRIBUTION REPORT
                      </div>
                      
                      <div class="summary">
                        <h3>Summary</h3>
                        <p><strong>Group:</strong> ${cleanGroupName(report.group?.name || "")}</p>
                        <p><strong>Month:</strong> ${monthYear}</p>
                        <p><strong>Total Members:</strong> ${report.summary?.totalTeachers || 0}</p>
                        <p><strong>Members with Declarations:</strong> ${report.summary?.teachersWithDeclarations || 0}</p>
                        <p><strong>Total Contributions:</strong> ${(report.summary?.totalContributions || 0).toLocaleString()} FRW</p>
                      </div>
                      
                      <table>
                        <thead>
                          <tr>
                            <th>Member Name</th>
                            <th>Status</th>
                            <th>Shares</th>
                            <th>Total Savings</th>
                            <th>Group Role</th>
                            <th>Contribution</th>
                            <th>Last Declaration</th>
                          </tr>
                        </thead>
                        <tbody>
                          ${tableRows}
                        </tbody>
                      </table>
                      
                      <div class="summary" style="margin-top: 20px;">
                        <strong>Total Members:</strong> ${report.summary?.totalTeachers || 0} | 
                        <strong>Total Contributions:</strong> ${(report.summary?.totalContributions || 0).toLocaleString()} FRW | 
                        <strong>Generated on:</strong> ${format(new Date(), 'MMMM d, yyyy')} at ${format(new Date(), 'h:mm a')}
                      </div>
                    `;
                    
                    // Create print styles
                    const printStyles = `
                      <style id="print-styles">
                        @media screen {
                          .print-container {
                            display: none !important;
                          }
                        }
                        @media print {
                          @page {
                            margin: 1cm;
                            size: A4;
                          }
                          body * {
                            visibility: hidden;
                          }
                          .print-container,
                          .print-container * {
                            visibility: visible;
                          }
                          .print-container {
                            position: absolute;
                            left: 0;
                            top: 0;
                            width: 100%;
                            background: white;
                            padding: 20px;
                            margin: 0;
                          }
                          body {
                            margin: 0 !important;
                            padding: 0 !important;
                          }
                          .print-header {
                            display: flex;
                            align-items: center;
                            margin-bottom: 15px;
                          }
                          .logo-container {
                            margin-right: 20px;
                          }
                          .logo-box {
                            width: 120px;
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                          }
                          .logo-box img {
                            max-width: 120px;
                            height: auto;
                            object-fit: contain;
                            border-radius: 50%;
                          }
                          .company-info {
                            flex: 1;
                          }
                          .company-name {
                            font-size: 24px;
                            font-weight: bold;
                            color: #000;
                            margin-bottom: 5px;
                          }
                          .report-title {
                            font-size: 18px;
                            font-weight: bold;
                            color: #000;
                            margin-bottom: 5px;
                          }
                          .date-range {
                            font-size: 14px;
                            color: #000;
                            margin-bottom: 0;
                          }
                          .separator-thick {
                            border: none;
                            border-top: 3px solid #000;
                            margin: 15px 0;
                            width: 100%;
                          }
                          .separator-thin {
                            border: none;
                            border-top: 1px solid #ccc;
                            margin: 15px 0 20px 0;
                            width: 100%;
                          }
                          .contact-info {
                            display: block;
                            font-size: 12px;
                            color: #000;
                            margin-bottom: 15px;
                          }
                          .contact-info div {
                            display: block;
                            margin-bottom: 5px;
                          }
                          table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 20px;
                            font-size: 11px;
                          }
                          th {
                            background-color: #4dbd07 !important;
                            color: white !important;
                            padding: 10px 6px;
                            text-align: left;
                            font-weight: 600;
                            border: 1px solid #3d9a06;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                          }
                          td {
                            border: 1px solid #ddd;
                            padding: 8px 6px;
                            text-align: left;
                          }
                          tbody tr:nth-child(even) {
                            background-color: #f9f9f9;
                          }
                          .summary {
                            margin-top: 20px;
                            padding: 15px;
                            background-color: #f5f5f5;
                            border-radius: 5px;
                            font-size: 14px;
                          }
                        }
                      </style>
                    `;
                    
                    try {
                      // Remove existing print container if any
                      const existingContainer = document.getElementById('print-container');
                      if (existingContainer) {
                        existingContainer.remove();
                      }
                      
                      // Remove existing print styles if any
                      const existingStyles = document.getElementById('print-styles');
                      if (existingStyles) {
                        existingStyles.remove();
                      }
                      
                      // Hide all page content temporarily
                      const originalBodyDisplay = document.body.style.display;
                      const allChildren = Array.from(document.body.children);
                      allChildren.forEach((child: any) => {
                        if (child.id !== 'print-container') {
                          child.style.display = 'none';
                        }
                      });
                      
                      // Add print styles to head
                      document.head.insertAdjacentHTML('beforeend', printStyles);
                      
                      // Create print container
                      const printContainer = document.createElement('div');
                      printContainer.id = 'print-container';
                      printContainer.className = 'print-container';
                      printContainer.style.position = 'absolute';
                      printContainer.style.left = '0';
                      printContainer.style.top = '0';
                      printContainer.style.width = '100%';
                      printContainer.style.background = 'white';
                      printContainer.style.padding = '20px';
                      printContainer.style.zIndex = '9999';
                      printContainer.innerHTML = printContent;
                      document.body.appendChild(printContainer);
                      
                      // Wait a moment for content to render, then trigger print
                      setTimeout(() => {
                        window.print();
                        
                        // Clean up after printing
                        setTimeout(() => {
                          if (printContainer && printContainer.parentNode) {
                            printContainer.remove();
                          }
                          const styles = document.getElementById('print-styles');
                          if (styles) {
                            styles.remove();
                          }
                          // Restore original display
                          allChildren.forEach((child: any) => {
                            child.style.display = '';
                          });
                          document.body.style.display = originalBodyDisplay;
                        }, 500);
                      }, 100);
                    } catch (error) {
                      console.error("Print error:", error);
                      toast.error("Failed to print report");
                      // Restore display on error
                      const allChildren = Array.from(document.body.children);
                      allChildren.forEach((child: any) => {
                        child.style.display = '';
                      });
                    }
                  } catch (error) {
                    console.error("Error generating print report:", error);
                    toast.error("Failed to generate print report");
                  }
                }}
              >
                <motion.span
                  className="flex items-center text-slate-700"
                  initial={{ x: 0 }}
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Printer className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                Print
                </motion.span>
              </motion.button>
              <motion.button
                className="h-8 sm:h-9 rounded-md px-1.5 sm:px-2 md:px-3 text-[10px] sm:text-xs md:text-[13px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 border border-green-700 min-w-[60px] sm:min-w-[80px] md:min-w-[100px] flex-shrink-0 bg-green-600 hover:bg-green-700"
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                onClick={handleExportCSV}
              >
                <motion.span
                  className="flex items-center text-white"
                  initial={{ x: 0 }}
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Download className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                  CSV
                </motion.span>
              </motion.button>
              <motion.button
                className="h-8 sm:h-9 rounded-md px-1.5 sm:px-2 md:px-3 text-[10px] sm:text-xs md:text-[13px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 border border-blue-800 min-w-[60px] sm:min-w-[80px] md:min-w-[100px] flex-shrink-0 bg-blue-700 hover:bg-blue-800"
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                onClick={handleDownloadPDF}
              >
                <motion.span
                  className="flex items-center text-white"
                  initial={{ x: 0 }}
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Download className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                  PDF
                </motion.span>
              </motion.button>
              <motion.button
                className="h-8 sm:h-9 rounded-md px-1.5 sm:px-2 md:px-3 text-[10px] sm:text-xs md:text-[13px] font-semibold flex items-center justify-center cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 border border-red-700 min-w-[60px] sm:min-w-[80px] md:min-w-[100px] flex-shrink-0 bg-red-600 hover:bg-red-700"
                whileHover={{ scale: 1.05, transition: { duration: 0.2 } }}
                whileTap={{ scale: 0.95, transition: { duration: 0.1 } }}
                onClick={handleClearPayroll}
              >
                <motion.span
                  className="flex items-center text-white"
                  initial={{ x: 0 }}
                  whileHover={{ x: 2 }}
                  transition={{ duration: 0.2 }}
                >
                  <Trash2 className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 mr-1 sm:mr-1.5 md:mr-2" />
                  Clear
                </motion.span>
              </motion.button>
              </div>
          </motion.div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as "contributions" | "loans" | "monthly-savings" | "loan-repayments")} className="space-y-6">
            <TabsList className="grid w-full max-w-lg grid-cols-4">
              <TabsTrigger value="contributions">Contributions</TabsTrigger>
              <TabsTrigger value="loans">Loans</TabsTrigger>
              <TabsTrigger value="monthly-savings">Monthly Savings</TabsTrigger>
              <TabsTrigger value="loan-repayments">Loan Repayments</TabsTrigger>
            </TabsList>

            {/* Contributions Tab */}
            <TabsContent value="contributions" className="space-y-6">
          {/* Print-only Header */}
          <div className="hidden print:block border-b-2 border-gray-800 pb-4 mb-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold uppercase">{cleanGroupName(report.group?.name || "")}</h1>
              <h2 className="text-xl font-semibold mt-2">Monthly Contribution Report</h2>
              <p className="text-sm mt-2">
                Month: <strong>{MONTHS[selectedMonth]} {selectedYear}</strong> | 
                Prepared By: {leadershipRole} - {currentUser.fullName || currentUser.firstName}
              </p>
            </div>
          </div>

          {/* Summary Cards */}
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-6 print:grid-cols-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Members</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{report.summary?.totalTeachers || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {report.summary?.teachersWithDeclarations || 0} members with declarations
                </p>
              </CardContent>
            </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: 0.3 }}
              whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
            >
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Contributions</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {(report.summary?.totalContributions || 0).toLocaleString()} FRW
                </div>
                <p className="text-xs text-muted-foreground">
                  Total contributions for this month
                </p>
              </CardContent>
            </Card>
            </motion.div>
          </motion.div>

          {/* Contribution Details Table */}
          <Card className="no-print-data-table">
            <CardContent className="text-sm">
              <div className="text-sm [&_table]:text-sm [&_td]:text-sm [&_th]:text-sm [&_span]:text-sm [&_button]:text-xs">
              <DataTable
                  title="Member Contributions"
                columns={columns}
                data={paginatedPayroll || []}
                isLoading={isLoading}
                defaultColumnVisibility={{}}
                facets={[
                  {
                    name: "declaredShares",
                    title: "Shares",
                    options: [
                      { label: "1 Share", value: 1 },
                      { label: "2 Shares", value: 2 },
                      { label: "3 Shares", value: 3 },
                      { label: "4 Shares", value: 4 },
                      { label: "5 Shares", value: 5 },
                    ],
                  },
                  {
                    name: "lastDeclarationDate",
                    title: "Declaration Status",
                    options: [
                      { label: "Declared", value: "declared" },
                      { label: "Not Declared", value: "Not declared" },
                    ],
                  },
                  {
                    name: "contributionAmount",
                    title: "Contribution Range",
                    options: [
                      { label: "10,000 FRW", value: 10000 },
                      { label: "20,000 FRW", value: 20000 },
                      { label: "30,000+ FRW", value: 30000 },
                      { label: "40,000+ FRW", value: 40000 },
                      { label: "50,000+ FRW", value: 50000 },
                    ],
                  },
                ]}
                onSearch={(query: string) => setSearchQuery(query)}
                columnFilters={columnFilters}
                setColumnFilters={setColumnFilters}
                setSorting={setSorting}
                sorting={sorting}
                setPagination={setPagination}
                pageIndex={pagination.pageIndex}
                pageSize={pagination.pageSize}
                pageCount={Math.ceil((filteredPayroll?.length || 0) / pagination.pageSize)}
                hidePagination={false}
                enableExport={false}
              />
              </div>
            </CardContent>
          </Card>

          {/* Print-Only Full Table (shows all filtered data) */}
          <div className="hidden print:block">
            <Card className="border-0 shadow-none">
              <CardContent className="p-0">
                <h3 className="text-lg font-bold mb-4">Member Contributions</h3>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-800 px-4 py-2 text-left font-bold">Member Name</th>
                      <th className="border border-gray-800 px-4 py-2 text-left font-bold">Shares</th>
                      <th className="border border-gray-800 px-4 py-2 text-left font-bold">Contribution</th>
                      <th className="border border-gray-800 px-4 py-2 text-left font-bold">Last Declaration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayroll.map((teacher, index) => (
                      <tr key={teacher.teacherId || index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        <td className="border border-gray-800 px-4 py-2">{teacher.teacherName}</td>
                        <td className="border border-gray-800 px-4 py-2">
                          {teacher.declaredShares || 0} {teacher.hasDelcared ? "" : "(auto)"}
                        </td>
                        <td className="border border-gray-800 px-4 py-2 font-bold">
                          {teacher.contributionAmount?.toLocaleString()} FRW
                        </td>
                        <td className="border border-gray-800 px-4 py-2">
                          {teacher.lastDeclarationDate ? new Date(teacher.lastDeclarationDate).toLocaleDateString() : "Not declared"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-200 font-bold">
                      <td className="border border-gray-800 px-4 py-2" colSpan={2}>TOTAL</td>
                      <td className="border border-gray-800 px-4 py-2">{(report.summary?.totalContributions || 0).toLocaleString()} FRW</td>
                      <td className="border border-gray-800 px-4 py-2">{report.summary?.totalTeachers || 0} members</td>
                    </tr>
                  </tfoot>
                </table>
              </CardContent>
            </Card>
          </div>

            </TabsContent>

            {/* Monthly Savings Tab */}
            <TabsContent value="monthly-savings" className="space-y-6">
              <MonthlySavingsPayroll selectedMonth={selectedMonth} selectedYear={selectedYear} />
            </TabsContent>

            {/* Loans Tab */}
            <TabsContent value="loans" className="space-y-6">
              {/* Print-only Header for Loans */}
              <div className="hidden print:block border-b-2 border-gray-800 pb-4 mb-6">
                <div className="text-center">
                  <h1 className="text-2xl font-bold uppercase">{cleanGroupName(report?.group?.name || "")}</h1>
                  <h2 className="text-xl font-semibold mt-2">Monthly Loan Payment Report</h2>
                  <p className="text-sm mt-2">
                    Month: <strong>{MONTHS[selectedMonth]} {selectedYear}</strong> | 
                    Prepared By: {leadershipRole} - {currentUser.fullName || currentUser.firstName}
                      </p>
                    </div>
                  </div>
                  
              {/* Loan Payment Declarations Summary Cards */}
              {loanPaymentDeclarationsData && (
                <motion.div 
                  className="grid grid-cols-1 md:grid-cols-3 gap-6 print:grid-cols-3"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 }}
                >
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.2 }}
                    whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                  >
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Total Declarations</CardTitle>
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold">
                          {loanPaymentDeclarationsData.summary?.total || 0}
                          </div>
                        <p className="text-xs text-muted-foreground">
                          Loan payment declarations
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.3 }}
                    whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                  >
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Approved</CardTitle>
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-green-600">
                          {loanPaymentDeclarationsData.summary?.approved || 0}
                          </div>
                        <p className="text-xs text-muted-foreground">
                          {(loanPaymentDeclarationsData.summary?.approvedAmount || 0).toLocaleString()} FRW
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.4 }}
                    whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                  >
                    <Card>
                      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Pending</CardTitle>
                        <AlertCircle className="h-4 w-4 text-yellow-600" />
                      </CardHeader>
                      <CardContent>
                        <div className="text-2xl font-bold text-yellow-600">
                          {loanPaymentDeclarationsData.summary?.pending || 0}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {(loanPaymentDeclarationsData.summary?.pendingAmount || 0).toLocaleString()} FRW
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                </motion.div>
              )}

              {/* Loan Payment Declarations Table */}
              {loanPaymentDeclarationsData && loanPaymentDeclarationsData.data && Array.isArray(loanPaymentDeclarationsData.data) && loanPaymentDeclarationsData.data.length > 0 ? (
                <>
                  {/* DataTable for Loan Payment Declarations */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                        Loan Payment Declarations - {loanPaymentDeclarationsData.summary?.monthName || ''} {loanPaymentDeclarationsData.summary?.year || ''}
                      </CardTitle>
                      <CardDescription>
                        All loan payment declarations for this month
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <DataTable
                        columns={loanPaymentDeclarationColumns}
                        data={loanPaymentDeclarationsData?.data || []}
                        isLoading={!loanPaymentDeclarationsData}
                        facets={[]}
                        defaultColumnVisibility={{}}
                        onSearch={() => {}}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        setSorting={setSorting}
                        sorting={sorting}
                        setPagination={setPagination}
                        pageIndex={pagination.pageIndex}
                        pageSize={pagination.pageSize}
                        pageCount={Math.ceil((loanPaymentDeclarationsData?.data?.length || 0) / pagination.pageSize)}
                      />
                    </CardContent>
                  </Card>

                  {/* Approval Button for Pending Declarations */}
                  {loanPaymentDeclarationsData.summary?.pending > 0 && (
                    <motion.div 
                      className="no-print mt-6 flex justify-center"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: 0.2 }}
                    >
                      <motion.button
                      onClick={() => loanPaymentModal.open({
                        id: groupId,
                          name: `${loanPaymentDeclarationsData.summary?.pending || 0} pending declaration(s)`,
                      })}
                      disabled={approveLoanPaymentsMutation.isLoading}
                        className="w-auto bg-blue-600 text-white hover:bg-blue-700 border-0 [&>span]:text-white [&_svg]:text-white py-2 px-4 rounded-md font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                        whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
                    >
                      {approveLoanPaymentsMutation.isLoading ? (
                          <span className="text-white">Approving...</span>
                      ) : (
                        <>
                            <CheckCircle className="w-4 h-4 mr-2 text-white" />
                            <span className="text-white">Approve {loanPaymentDeclarationsData.summary?.pending || 0} Pending Loan Payment Declaration(s)</span>
                        </>
                      )}
                      </motion.button>
                    </motion.div>
                  )}
                </>
              ) : (
                <Card>
                  <CardContent className="py-8 text-center">
                    <CreditCard className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-lg font-semibold">No Loan Payment Declarations</p>
                    <p className="text-muted-foreground mt-2">
                      No loan payment declarations found for {MONTHS[selectedMonth]} {selectedYear}
                    </p>
              </CardContent>
            </Card>
          )}
            </TabsContent>

            {/* Loan Repayments Tab */}
            <TabsContent value="loan-repayments" className="space-y-6">
              <LoanRepaymentsPayroll selectedMonth={selectedMonth} selectedYear={selectedYear} />
            </TabsContent>
          </Tabs>

        </div>

        {/* Print Styles */}
        <style>{`
          @media print {
            @page {
              margin: 1cm;
            }
            body {
              background: white !important;
            }
            .no-print {
              display: none !important;
            }
            /* Hide the paginated DataTable in print mode */
            .no-print-data-table {
              display: none !important;
            }
            /* Show print-only elements */
            .print\\:block {
              display: block !important;
            }
            table {
              page-break-inside: auto;
              width: 100%;
              border-collapse: collapse;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            thead {
              display: table-header-group;
            }
            tfoot {
              display: table-footer-group;
            }
            /* Remove borders from card containers in print, but keep table cell borders */
            #print-container .card,
            #print-container [class*="Card"],
            #print-container [class*="card"] {
              border: none !important;
              box-shadow: none !important;
              page-break-inside: avoid;
            }
            /* Remove outer border from tables */
            #print-container table {
              border: none !important;
            }
            /* Keep cell borders */
            #print-container table th,
            #print-container table td {
              border: 1px solid #000 !important;
            }
            /* Force summary cards to stay on same row in print */
            #print-container .grid {
              display: grid !important;
            }
            #print-container .grid.grid-cols-1 {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            }
            /* Hide all icons in print */
            #print-container svg,
            #print-container [class*="lucide"] {
              display: none !important;
            }
            /* Hide any anchor links or URLs in print */
            a[href]::after {
              content: none !important;
            }
            a[href]:after {
              content: none !important;
            }
          }
        `}</style>
      </div>
    </>
  );
}


